/* START_MODULE_CONTRACT
 * PURPOSE: Frontend UI module: EditEmployeeDialog.
 * SCOPE: React component or page.
 * DEPENDS: M-FE-API, M-FE-AUTH
 * LINKS: M-FE-EMPLOYEES
 * END_MODULE_CONTRACT
 *
 * START_MODULE_MAP
 * EditEmployeeDialog - Диалог редактирования сотрудника с превью и секциями.
 * END_MODULE_MAP
 *
 * START_CHANGE_SUMMARY
 * LAST_CHANGE: 2026-07-14 - Вертикальный скролл длинной формы через formDialogContentSx.
 * LAST_CHANGE: 2026-07-14 - UX: шапка команды, live-превью, секции формы, убрано дублирующее поле проекта.
 * LAST_CHANGE: 2026-07-13 - UX: submit error alert, loading-кнопка, Enter-submit, inline-валидация имени.
 * END_CHANGE_SUMMARY
 */

import { useState, useEffect, useRef } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import Collapse from '@mui/material/Collapse';
import { alpha } from '@mui/material/styles';
import FormDialogHeader, {
  formDialogActionsSx,
  formDialogContentSx,
  formDialogFormSx,
  formDialogPaperSlotProps,
} from '../../../components/FormDialogHeader';
import FormSection from '../../../components/FormSection';
import ProjectAvatar from '../../../components/ProjectAvatar';
import RoleWorkloadBar from '../../../components/RoleWorkloadBar';
import { getColorByName } from '../../../utils/projectColors';
import { useDialogFormReady } from '../../../hooks/useDialogFormReady';
import { useBlurValidatedField } from '../../../hooks/useBlurValidatedField';
import { isNonEmptyTrimmed } from '../../../utils/formValidators';
import ProjectRoleSelect from './ProjectRoleSelect';
import EmployeeAbsenceFields, {
  absencesToFormState,
  areAllAbsencesValid,
  buildAbsencesPayload,
} from './EmployeeAbsenceFields';

/* START_METHOD_EditEmployeeDialog */
/* START_CONTRACT: EditEmployeeDialog
 * PURPOSE: Диалог редактирования сотрудника (имя, роль, отсутствия) с предзаполнением и обратной связью по сохранению.
 * INPUTS: { open: boolean, onClose: ()=>void, onUpdate: (id,payload)=>Promise, employee: Employee, project: Project, availableRoles: ProjectRole[], allRoles: ProjectRole[] }.
 * OUTPUTS: { JSX.Element|null - диалог }.
 * SIDE_EFFECTS: вызов onUpdate (HTTP наверх); локальный state.
 * LINKS: M-FE-EMPLOYEES
 * END_CONTRACT: EditEmployeeDialog
 */
export default function EditEmployeeDialog({
  open,
  onClose,
  onUpdate,
  employee,
  project,
  availableRoles,
  allRoles,
}) {
  const firstFieldRef = useRef(null);
  const { formReady, resetFormReady, dialogTransitionSlotProps } = useDialogFormReady({ firstFieldRef });
  const nameField = useBlurValidatedField('', isNonEmptyTrimmed, formReady);
  const [selectedRole, setSelectedRole] = useState(null);
  const [absences, setAbsences] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    if (!employee) return;
    nameField.reset(employee.name);
    setSubmitError('');
    const role = allRoles.find((r) => r.id === employee.projectRoleId) ?? null;
    setSelectedRole(role);
    setAbsences(absencesToFormState(employee.absences));
  }, [employee, allRoles]);

  if (!employee) return null;

  const trimmedName = nameField.value.trim();
  const previewName = trimmedName || employee.name;
  const roleColor = selectedRole ? getColorByName(selectedRole.name) : null;

  const selectableRoles = selectedRole
    ? [selectedRole, ...availableRoles.filter((r) => r.id !== selectedRole.id)]
    : availableRoles;

  const canSave = nameField.isValid
    && selectedRole != null
    && areAllAbsencesValid(absences);

  const handleClose = () => {
    if (loading) return;
    resetFormReady();
    onClose();
  };

  const handleUpdate = async (event) => {
    if (event) event.preventDefault();
    if (!canSave || loading) {
      nameField.touch();
      return;
    }
    setLoading(true);
    setSubmitError('');
    try {
      await onUpdate(employee.id, {
        name: trimmedName,
        projectRoleId: selectedRole.id,
        absences: buildAbsencesPayload(absences),
      });
      onClose();
    } catch (err) {
      console.warn('[EditEmployeeDialog][handleUpdate][SUBMIT_FAIL] update employee failed', {
        status: err.response?.status,
      });
      setSubmitError(
        err.response?.data?.message || 'Не удалось сохранить изменения. Попробуйте ещё раз.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="edit-employee-title"
      slotProps={formDialogPaperSlotProps(dialogTransitionSlotProps)}
    >
      <Box
        component="form"
        onSubmit={handleUpdate}
        noValidate
        sx={formDialogFormSx}
      >
        <FormDialogHeader
          overline="Команда"
          title="Редактирование сотрудника"
          titleId="edit-employee-title"
          subtitle={
            project?.name
              ? `Изменения в проекте «${project.name}» влияют на планирование загрузки.`
              : 'Обновите роль или периоды отсутствия.'
          }
        />

        <DialogContent sx={formDialogContentSx}>
          <Collapse in={Boolean(submitError)} unmountOnExit>
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubmitError('')}>
              {submitError}
            </Alert>
          </Collapse>

          <Paper
            variant="outlined"
            aria-hidden
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 2,
              py: 1.5,
              mb: 2.5,
              borderColor: roleColor ? alpha(roleColor, 0.25) : 'divider',
              bgcolor: roleColor ? alpha(roleColor, 0.04) : 'background.default',
            }}
          >
            <ProjectAvatar name={trimmedName || employee.name} size={36} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle2" fontWeight={600} noWrap>
                {previewName}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {selectedRole ? selectedRole.name : 'Роль не назначена'}
              </Typography>
              {selectedRole && (
                <Box sx={{ mt: 0.75 }}>
                  <RoleWorkloadBar
                    value={selectedRole.workloadPercent}
                    accentColor={roleColor}
                    size="lg"
                  />
                </Box>
              )}
            </Box>
            {selectedRole && (
              <Chip
                label={selectedRole.shortName}
                size="small"
                sx={{
                  height: 22,
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  bgcolor: roleColor,
                  color: '#fff',
                  flexShrink: 0,
                }}
              />
            )}
          </Paper>

          <FormSection label="О сотруднике" first>
            <TextField
              inputRef={firstFieldRef}
              required
              fullWidth
              label="Имя"
              value={nameField.value}
              onChange={nameField.onChange}
              onBlur={nameField.onBlur}
              size="small"
              disabled={loading}
              error={nameField.showError}
              helperText={nameField.showError ? 'Введите имя сотрудника' : ' '}
              slotProps={{ htmlInput: { maxLength: 200 } }}
            />
            <ProjectRoleSelect
              label="Проектная роль"
              roles={selectableRoles}
              value={selectedRole}
              onChange={setSelectedRole}
              disabled={loading}
            />
          </FormSection>

          <FormSection label="Отсутствия">
            <EmployeeAbsenceFields
              absences={absences}
              onChange={setAbsences}
              disabled={loading}
            />
          </FormSection>
        </DialogContent>

        <DialogActions sx={formDialogActionsSx}>
          <Button size="small" onClick={handleClose} disabled={loading}>
            Отмена
          </Button>
          <Button
            type="submit"
            variant="contained"
            size="small"
            loading={loading}
            disabled={!canSave}
          >
            Сохранить
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
/* END_METHOD_EditEmployeeDialog */
